import { Link } from "react-router-dom";
import { ScrollVideo } from "@/components/ScrollVideo";
import { Reveal } from "@/components/Reveal";
import { ProductImage } from "@/components/ProductImage";
import { useProducts } from "@/lib/useProducts";
import { useSeo } from "@/lib/seo";
import { useLang, lp } from "@/lib/i18n";
import { VIDEO, POSTER } from "@/assets";

const jost = { fontFamily: "Jost, sans-serif" };

const T = {
  fr: {
    seoTitle: "Domino — la voie de l'équilibre · Maison Partita",
    seoDesc: "Vingt-quatre eaux de parfum, deux séries miroir de douze. Le numéro n'est pas écrit, il est porté par les points de la tuile.",
    kicker: "La voie de l'équilibre",
    intro1: "Vingt-quatre eaux de parfum, deux séries miroir de douze : la Blanche et la Noire. Le même numéro existe des deux côtés du jeu — la Blanche tire vers les matières lumineuses, la Noire vers les matières sombres. Une tuile blanche et sa jumelle noire ne sont pas deux versions d'un parfum : ce sont deux réponses à la même question.",
    intro2: <>Le principe fondateur : le numéro n'est pas écrit, il est porté par les points de la tuile, répartis entre les deux moitiés comme sur un vrai domino. La boîte <em>est</em> la tuile.</>,
    format: "Eau de parfum concentrée · 50 ml · Série limitée numérotée.",
    film: "Film · La voie de l'équilibre",
    blanche: "Série Blanche", blancheNote: "Matières lumineuses",
    noire: "Série Noire", noireNote: "Matières sombres",
    soldOut: "Épuisé",
    altEtui: (roman, serie, pips) => `Étui DOMINO ${roman}, série ${serie}, tuile à ${pips[0]} points et ${pips[1]} points`,
  },
  en: {
    seoTitle: "Domino — the way of balance · Maison Partita",
    seoDesc: "Twenty-four eaux de parfum, two mirrored series of twelve. The number is not written; it is carried by the pips of the tile.",
    kicker: "The way of balance",
    intro1: "Twenty-four eaux de parfum, two mirrored series of twelve: the White and the Black. The same number exists on both sides of the game — the White leans toward luminous materials, the Black toward dark ones. A white tile and its black twin are not two versions of one perfume: they are two answers to the same question.",
    intro2: <>The founding principle: the number is not written, it is carried by the pips of the tile, split between the two halves as on a true domino. The box <em>is</em> the tile.</>,
    format: "Concentrated eau de parfum · 50 ml · Numbered limited series.",
    film: "Film · The way of balance",
    blanche: "White Series", blancheNote: "Luminous materials",
    noire: "Black Series", noireNote: "Dark materials",
    soldOut: "Sold out",
    altEtui: (roman, serie, pips) => `DOMINO ${roman} case, ${serie} series, tile with ${pips[0]} and ${pips[1]} pips`,
  },
};

const Serie = ({ title, note, items, lang, t }) => (
  <section className="max-w-[1400px] mx-auto px-5 sm:px-10 pb-24">
    <Reveal>
      <div className="flex items-baseline justify-between border-b border-black/10 pb-4">
        <h2 className="text-2xl tracking-[0.24em] uppercase text-black" style={jost}>{title}</h2>
        <span className="text-[13px] tracking-[0.16em] uppercase text-black/45" style={jost}>{note}</span>
      </div>
    </Reveal>
    <div className="mt-14 grid gap-x-8 gap-y-16 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((p, i) => {
        const serie = p.serie === "noire" ? "serie-noire" : "serie-blanche";
        return (
          <Reveal key={p.id} delay={(i % 4) * 0.08}>
            <Link to={lp(lang, `/domino/${serie}/${p.slug}`)} data-testid={`domino-${p.serie}-${p.slug}`} className="group block">
              <div className="overflow-hidden border border-black/10" style={{ background: "#efece3" }}>
                <ProductImage maison="domino" reference={p.reference} suffix="etui"
                  alt={t.altEtui(p.roman, p.serie, p.pips)}
                  className={`transition-opacity duration-[1000ms] ${p.available ? "opacity-100" : "opacity-80 group-hover:opacity-100"}`} />
              </div>
              <div className="mt-4" style={jost}>
                <h3 className="text-[15px] tracking-[0.2em] uppercase text-black">DOMINO {p.roman}</h3>
                {p.available
                  ? <p className="mt-1 text-black/70">{p.price} €</p>
                  : <p className="mt-1 text-[12px] tracking-[0.2em] uppercase text-black/40">{t.soldOut}</p>}
              </div>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export default function Domino() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const { products } = useProducts("domino");
  const blanche = products.filter((p) => p.serie === "blanche");
  const noire = products.filter((p) => p.serie === "noire");

  return (
    <div>
      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 pb-24" style={jost}>
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase text-black/55">{t.kicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-[0.12em] uppercase text-black">Domino</h1>
          <p className="mt-10 text-[19px] leading-[1.8] text-black/75">{t.intro1}</p>
          <p className="mt-8 text-[19px] leading-[1.8] text-black/75">{t.intro2}</p>
          <p className="mt-6 text-[16px] text-black/50">{t.format}</p>
        </Reveal>
      </section>

      <ScrollVideo src={VIDEO.domino} poster={POSTER.domino} heightVh={180}>
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-10 flex items-end pb-16">
          <p className="text-[13px] tracking-[0.34em] uppercase text-zinc-300" style={{ fontFamily: "Jost, sans-serif" }}>{t.film}</p>
        </div>
      </ScrollVideo>

      <Serie title={t.blanche} note={t.blancheNote} items={blanche} lang={lang} t={t} />
      <Serie title={t.noire} note={t.noireNote} items={noire} lang={lang} t={t} />
    </div>
  );
}
